/**
 * Weaviate Management Page
 *
 * Admin page for managing Weaviate vector database tenants and sync state.
 * Provides tools for:
 * - Monitoring sync status, reembed progress, and entity sync breakdown
 * - Triggering reconciliation and re-embed runs
 * - Listing and deleting tenants by prefix (dev/staging cleanup)
 * - Resetting sync state for background reconciliation jobs
 */

import { PageController } from '@client/components/_core/layout/PageController';
import { showJobSuccessModal } from '@client/components/_shared/jobs/JobSuccessModal';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Progress } from '@client/components/_ui/feedback/Progress';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useDeleteConfirmation } from '@client/hooks/ui/useDeleteConfirmation';
import { selectIsSuperAdmin, useAuthStore } from '@client/stores/AuthStore';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import {
    Alert,
    Space as AntSpace,
    Badge,
    Card,
    Collapse,
    Divider,
    Empty,
    Input,
    InputNumber,
    Select,
    Skeleton,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    Activity,
    AlertTriangle,
    Ban,
    Brain,
    CheckCircle2,
    Clock,
    Eraser,
    HelpCircle,
    Play,
    RefreshCw,
    Search,
    Settings,
    ShieldAlert,
    Trash2,
    XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { V1CleanupCard } from './weaviate-cleanup/V1CleanupCard';

const { Text, Title } = Typography;

const TAB_KEYS = ['status', 'quarantine', 'configuration', 'operations'] as const;
type TabKey = (typeof TAB_KEYS)[number];

export const Route = createFileRoute('/admin-console/advanced/weaviate')({
    staticData: {
        ui: {
            title: 'Weaviate management',
            menuDescription: 'Manage Weaviate tenants and sync state',
        },
        breadcrumb: { title: 'Weaviate management' },
        permissions: ['super:admin'],
    },
    validateSearch: zodValidator(
        z.object({
            tab: z.enum(TAB_KEYS).optional(),
        }),
    ),
    component: WeaviateManagementPage,
});

/**
 * Route guard: Weaviate management only makes sense for super administrators in the admin
 * console — every endpoint it calls is a `superAdminProcedure`. The admin-console org context
 * (and the super-admin role granted within it) is switched in asynchronously by RequireAuth
 * *after* this route loads, so a `beforeLoad` guard would race that switch and wrongly redirect
 * a legitimate admin on first navigation. Gate at the component instead: `useAuthStore` is
 * reactive, so it settles once the org switch completes and, crucially, re-hides the page the
 * moment the user switches back to a tenant org — the exact transition that produced the 403
 * `getSyncStatus` poll-loop in Sentry CURIUM-API-W8.
 */
function WeaviateManagementPage() {
    const hasAccess = useAuthStore(
        (state) =>
            state.pendingOrgId === undefined &&
            state.user?.orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE &&
            state.org?.id === SYSTEM_ORG_IDS.SYSTEM_CONSOLE &&
            selectIsSuperAdmin(state),
    );

    if (!hasAccess) {
        return (
            <PageController route={Route}>
                <div className="mx-auto max-w-5xl p-6">
                    <Alert
                        type="warning"
                        showIcon
                        message="Super administrator access required"
                        description="Weaviate management is only available to super administrators in the admin console. Switch to a super admin account to continue."
                    />
                </div>
            </PageController>
        );
    }

    return <WeaviateManagementContent />;
}

function WeaviateManagementContent() {
    const { trpc } = useTrpc();
    const { tab } = Route.useSearch();
    const navigate = useNavigate();
    const activeTab: TabKey = tab ?? 'status';
    // Cheap query already on the page (status tab uses a 30s refetch); reuse
    // here so the Quarantine tab label can carry a live count badge without a
    // second roundtrip.
    const quarantineSummary = trpc.admin.weaviate.listQuarantined.useQuery({ limit: 1 }, { refetchInterval: 60_000 });
    const quarantineTotal = quarantineSummary.data?.totals.total ?? 0;

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-5xl p-6">
                <Card className="mb-6">
                    <Title level={4} className="!mb-1">
                        Weaviate management
                    </Title>
                    <Text type="secondary">Manage Weaviate vector database tenants and background sync jobs</Text>
                </Card>

                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        navigate({
                            to: '/admin-console/advanced/weaviate',
                            search: { tab: key as TabKey },
                            replace: true,
                        });
                    }}
                    size="large"
                    items={[
                        {
                            key: 'status',
                            label: (
                                <Space size="small">
                                    <Activity size={14} />
                                    Status
                                </Space>
                            ),
                            children: <SyncStatusSection />,
                        },
                        {
                            key: 'quarantine',
                            label: (
                                <Space size="small">
                                    <ShieldAlert size={14} />
                                    Quarantine
                                    {quarantineTotal > 0 && (
                                        <Badge count={quarantineTotal} overflowCount={999} size="small" />
                                    )}
                                </Space>
                            ),
                            children: <QuarantinedRecordsSection />,
                        },
                        {
                            key: 'configuration',
                            label: (
                                <Space size="small">
                                    <Settings size={14} />
                                    Configuration
                                </Space>
                            ),
                            children: <ReembedConfigSection />,
                        },
                        {
                            key: 'operations',
                            label: (
                                <Space size="small">
                                    <Trash2 size={14} />
                                    Operations
                                </Space>
                            ),
                            children: (
                                <>
                                    <ObligationBackfillSection />
                                    <TenantManagementSection />
                                    <SyncStateSection />
                                </>
                            ),
                        },
                    ]}
                />
            </div>
        </PageController>
    );
}

// ─── Sync Status Dashboard ──────────────────────────────────────────────────

type SyncStatusData = RouterOutputs['admin']['weaviate']['getSyncStatus'];

const DEFAULT_MAX_DOCUMENTS = 2000;
const MAX_DOCUMENTS_INPUT_ID = 'weaviate-max-documents-input';
const TENANT_PREFIX_INPUT_ID = 'weaviate-tenant-prefix-input';

function SyncStatusSection() {
    const { trpc, trpcClient } = useTrpc();
    const { data, isLoading, isFetching, refetch } = trpc.admin.weaviate.getSyncStatus.useQuery(undefined, {
        refetchInterval: 30_000,
    });
    const [isTriggering, setIsTriggering] = useState<'reconciliation' | 'reembed' | 'backfill' | null>(null);
    const [maxDocuments, setMaxDocuments] = useState<number>(DEFAULT_MAX_DOCUMENTS);

    const handleTriggerReconciliation = async () => {
        try {
            setIsTriggering('reconciliation');
            message.loading('Running reconciliation...');
            const result = await trpcClient.admin.weaviate.triggerReconciliation.mutate({ maxDocuments });
            message.destroy();
            const totalProcessed = Object.values(result.processed).reduce((a, b) => a + b, 0);
            const totalCreated = Object.values(result.created).reduce((a, b) => a + b, 0);
            message.success(
                `Reconciliation complete — ${totalProcessed} processed, ${totalCreated} created, ${result.errors} error(s)`,
            );
            refetch();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        } finally {
            setIsTriggering(null);
        }
    };

    const handleTriggerReembed = async () => {
        try {
            setIsTriggering('reembed');
            message.loading('Running re-embed batch...');
            await trpcClient.admin.weaviate.triggerReembed.mutate();
            message.destroy();
            message.success('Re-embed batch complete');
            refetch();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        } finally {
            setIsTriggering(null);
        }
    };

    const handleResetAndReembed = async () => {
        try {
            setIsTriggering('reembed');
            message.loading('Resetting re-embed state...');
            await trpcClient.admin.weaviate.resetSyncState.mutate({
                confirmationText: 'RESET',
                settings: ['weaviate_reembed_state'],
            });
            message.destroy();
            message.loading('Starting re-embed...');
            await trpcClient.admin.weaviate.triggerReembed.mutate();
            message.destroy();
            message.success('Re-embed reset and first batch complete');
            refetch();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        } finally {
            setIsTriggering(null);
        }
    };

    const handleTriggerBackfill = async () => {
        try {
            setIsTriggering('backfill');
            message.loading('Queueing v2 backfill...');
            await trpcClient.admin.weaviate.triggerV2Backfill.mutate();
            message.destroy();
            message.success('V2 backfill queued');
            refetch();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        } finally {
            setIsTriggering(null);
        }
    };

    const handleResetAndBackfill = async () => {
        try {
            setIsTriggering('backfill');
            message.loading('Resetting v2 backfill state...');
            await trpcClient.admin.weaviate.resetSyncState.mutate({
                confirmationText: 'RESET',
                settings: ['weaviate_v2_backfill_state'],
            });
            message.destroy();
            message.loading('Queueing v2 backfill...');
            await trpcClient.admin.weaviate.triggerV2Backfill.mutate();
            message.destroy();
            message.success('V2 backfill reset and queued');
            refetch();
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        } finally {
            setIsTriggering(null);
        }
    };

    return (
        <div className="mb-6">
            <Divider>
                <Space>
                    <Activity size={16} />
                    <Title level={5} className="!mb-0">
                        Sync status
                    </Title>
                </Space>
            </Divider>

            {isLoading ? (
                <Card size="small">
                    <Skeleton active />
                </Card>
            ) : data ? (
                <div className="space-y-4">
                    {/* Cluster + Vectoriser */}
                    <Card size="small">
                        <div className="mb-3 flex items-center justify-between">
                            <div className="flex flex-wrap items-center gap-3">
                                {data.cluster.version && <Tag variant="outlined">Weaviate {data.cluster.version}</Tag>}
                                {data.collectionPrefix ? (
                                    <Tag color="purple" variant="outlined">
                                        Prefix: {data.collectionPrefix}
                                    </Tag>
                                ) : (
                                    <Tag variant="outlined">No prefix (production)</Tag>
                                )}
                                <div className="flex items-center gap-2">
                                    <Brain size={14} className="text-subtle-foreground" />
                                    <Tag color="blue" variant="outlined">
                                        {data.vectoriser.model}
                                    </Tag>
                                    <Tag variant="outlined">{data.vectoriser.dimensions}d</Tag>
                                    <Tag variant="outlined">{data.vectoriser.maxTokens} tokens</Tag>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CircuitBreakerBadge state={data.circuitBreaker.state} />
                                    {data.failureCount > 0 && (
                                        <Tag color="red" variant="outlined">
                                            {data.failureCount} failure(s)
                                        </Tag>
                                    )}
                                </div>
                            </div>
                            <Button type="secondary" size="small" loading={isFetching} onClick={() => refetch()}>
                                Refresh
                            </Button>
                        </div>

                        {/* Available models */}
                        <div className="flex flex-wrap items-center gap-2">
                            <Text type="secondary" className="text-xs">
                                Available models:
                            </Text>
                            {data.knownModels.map((m) => (
                                <Tooltip key={m.id} title={`${m.description} (${m.tokens} tokens)`}>
                                    <Tag
                                        variant="outlined"
                                        color={m.id === data.vectoriser.model ? 'green' : undefined}
                                        className="!text-xs"
                                    >
                                        {m.id}
                                        {m.id === data.vectoriser.model && ' (active)'}
                                    </Tag>
                                </Tooltip>
                            ))}
                        </div>

                        {/* Enabled modules */}
                        {data.cluster.modules && Object.keys(data.cluster.modules).length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Text type="secondary" className="text-xs">
                                    Modules:
                                </Text>
                                {Object.keys(data.cluster.modules).map((mod) => (
                                    <Tag key={mod} variant="outlined" className="!text-xs">
                                        {mod}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Card>

                    {/* Reembed Progress */}
                    <ReembedProgressCard
                        reembed={data.reembed}
                        reembedConfig={data.reembedConfig}
                        isTriggering={isTriggering === 'reembed'}
                        onTrigger={handleTriggerReembed}
                        onResetAndReembed={handleResetAndReembed}
                    />

                    {/* V2 Backfill Progress */}
                    <V2BackfillCard
                        backfill={data.backfill}
                        mirrorHealth={data.mirrorHealth}
                        reembedConfig={data.reembedConfig}
                        isTriggering={isTriggering === 'backfill'}
                        onTrigger={handleTriggerBackfill}
                        onResetAndBackfill={handleResetAndBackfill}
                    />

                    {/* v1 vs v2 cleanup (CUR-862) */}
                    <V1CleanupCard />

                    {/* Entity Sync Status */}
                    <EntitySyncStatusCard
                        entitySyncStatus={data.entitySyncStatus}
                        onRefresh={refetch}
                        isFetching={isFetching}
                    />

                    {/* Actions */}
                    <Card size="small">
                        <Text type="secondary" className="mb-3 block">
                            Manual triggers run immediately outside the cron schedule. <strong>Max documents</strong>{' '}
                            caps how many Postgres rows the run will scan (in batches of 200) across all collections —
                            not just pending ones. Most rows are already synced and skip quickly. The run stops earlier
                            once every collection's cursor reaches the end.
                        </Text>
                        <Space>
                            <AntSpace.Compact className="w-72">
                                <label
                                    htmlFor={MAX_DOCUMENTS_INPUT_ID}
                                    className="inline-flex items-center rounded-l-md border border-separator border-r-0 bg-muted px-3 text-subtle-foreground"
                                >
                                    Max documents
                                </label>
                                <InputNumber
                                    id={MAX_DOCUMENTS_INPUT_ID}
                                    min={200}
                                    max={50000}
                                    step={1000}
                                    value={maxDocuments}
                                    onChange={(v) => setMaxDocuments(v ?? DEFAULT_MAX_DOCUMENTS)}
                                    className="flex-1"
                                />
                            </AntSpace.Compact>
                            <Confirm
                                title="Trigger reconciliation"
                                description={
                                    <span>
                                        This will scan up to <strong>{maxDocuments.toLocaleString()}</strong> Postgres
                                        rows in cursor order, syncing any that are missing or stale in Weaviate. On a
                                        cold cursor the budget can be spent before reaching pending rows — re-run if the
                                        dashboard still shows pending after a single pass.
                                        {maxDocuments > 5000 && (
                                            <>
                                                {' '}
                                                <strong>Note:</strong> large runs may take several minutes. The HTTP
                                                request times out after 5 minutes; if that happens, the work already
                                                done is persisted via cursor and the next run continues.
                                            </>
                                        )}
                                    </span>
                                }
                                onOk={handleTriggerReconciliation}
                                okText="Run reconciliation"
                            >
                                <Button
                                    type="secondary"
                                    icon={<Play size={14} />}
                                    loading={isTriggering === 'reconciliation'}
                                >
                                    Run reconciliation
                                </Button>
                            </Confirm>
                        </Space>
                    </Card>
                </div>
            ) : null}
        </div>
    );
}

function CircuitBreakerBadge({ state }: { state: string }) {
    if (state === 'CLOSED') {
        return (
            <Tag color="green" variant="outlined">
                <Space size="micro">
                    <CheckCircle2 size={12} />
                    Healthy
                </Space>
            </Tag>
        );
    }
    if (state === 'OPEN') {
        return (
            <Tag color="red" variant="outlined">
                <Space size="micro">
                    <XCircle size={12} />
                    Circuit open
                </Space>
            </Tag>
        );
    }
    return (
        <Tag color="orange" variant="outlined">
            <Space size="micro">
                <Clock size={12} />
                Half-open
            </Space>
        </Tag>
    );
}

type ReembedData = SyncStatusData['reembed'];
type ReembedStatus = ReembedData['status'];
type ReembedState = NonNullable<ReembedData['state']>;
type PhaseProgress = { processed: number; total?: number };

function PhaseProgressBar({ progress }: { progress: PhaseProgress }) {
    const { processed, total } = progress;
    if (total === undefined || total === 0) {
        return (
            <div className="mb-2 text-subtle-foreground text-xs">
                Phase progress: <strong>{processed.toLocaleString()}</strong> processed (total unknown)
            </div>
        );
    }
    const percent = Math.min(100, Math.round((processed / total) * 100));
    return (
        <div className="mb-2">
            <div className="mb-1 text-subtle-foreground text-xs">
                Phase progress: <strong>{processed.toLocaleString()}</strong> of {total.toLocaleString()} ({percent}%)
            </div>
            <Progress percent={percent} status="active" size="small" showInfo={false} />
        </div>
    );
}

function OverallProgressBar({
    state,
    status,
    variant = 'reembed',
}: {
    state: ReembedState;
    status: ReembedStatus;
    variant?: 'reembed' | 'backfill';
}) {
    const phaseTotals = state.phaseTotals;
    const phaseProcessed = state.phaseProcessed;
    if (!phaseTotals || Object.keys(phaseTotals).length === 0) return null;

    let processedSum = 0;
    let totalSum = 0;
    for (const [key, total] of Object.entries(phaseTotals)) {
        totalSum += total;
        processedSum += phaseProcessed?.[key] ?? 0;
    }
    if (totalSum === 0) return null;

    const isComplete = status.kind === 'complete';
    const hasCompletedWithErrors = isComplete && state.totals.errors > 0;
    const percent = isComplete ? 100 : Math.min(100, Math.round((processedSum / totalSum) * 100));
    const displayProcessed = isComplete ? state.totals.processed : processedSum;
    const progressStatus = (() => {
        if (hasCompletedWithErrors) return 'exception';
        if (isComplete) return 'success';
        return 'active';
    })();
    return (
        <div className="mb-3">
            <div className="mb-1 text-subtle-foreground text-xs">
                {hasCompletedWithErrors ? (
                    <>
                        {variant === 'backfill' ? 'Backfill' : 'Re-embed'} complete with{' '}
                        <strong>{state.totals.errors.toLocaleString()}</strong> error(s); processed{' '}
                        <strong>{displayProcessed.toLocaleString()}</strong> records.
                    </>
                ) : isComplete ? (
                    <>
                        {variant === 'backfill' ? 'Backfilled' : 'Re-embedded'} all{' '}
                        <strong>{displayProcessed.toLocaleString()}</strong> records.
                    </>
                ) : (
                    <>
                        Overall: <strong>{processedSum.toLocaleString()}</strong> of {totalSum.toLocaleString()} (
                        {percent}%)<span className="ml-1">— approximate; phases differ in size.</span>
                    </>
                )}
            </div>
            <Progress percent={percent} status={progressStatus} size="small" showInfo={false} />
        </div>
    );
}

function StatusTag({ status }: { status: ReembedStatus }) {
    switch (status.kind) {
        case 'never-run':
            return (
                <Tag variant="outlined">
                    <Space size="micro">
                        <Clock size={12} />
                        Never run
                    </Space>
                </Tag>
            );
        case 'running':
            return (
                <Tag color="blue" variant="outlined">
                    <Space size="micro">
                        <RefreshCw size={12} className="animate-spin" />
                        Running now
                    </Space>
                </Tag>
            );
        case 'idle':
            return (
                <Tag color="geekblue" variant="outlined">
                    <Space size="micro">
                        <Clock size={12} />
                        Active — between ticks
                    </Space>
                </Tag>
            );
        case 'stalled':
            return (
                <Tag color="orange" variant="outlined">
                    <Space size="micro">
                        <AlertTriangle size={12} />
                        Stalled
                    </Space>
                </Tag>
            );
        case 'blocked':
            return (
                <Tag color="volcano" variant="outlined">
                    <Space size="micro">
                        <Ban size={12} />
                        Blocked
                    </Space>
                </Tag>
            );
        case 'failed':
            return (
                <Tag color="red" variant="outlined">
                    <Space size="micro">
                        <XCircle size={12} />
                        Failed
                    </Space>
                </Tag>
            );
        case 'complete':
            return (
                <Tag color="green" variant="outlined">
                    <Space size="micro">
                        <CheckCircle2 size={12} />
                        Complete
                    </Space>
                </Tag>
            );
    }
}

function statusDescription(status: ReembedStatus, throughputPerRun: number): string {
    switch (status.kind) {
        case 'never-run':
            return `Re-embedding writes all Postgres entities into Weaviate with fresh embeddings. The job runs automatically every 10 minutes — up to ~${throughputPerRun.toLocaleString()} records per run. The first tick starts the campaign; you can also trigger it manually below.`;
        case 'running':
            return `Currently re-embedding ${status.phaseLabel}. This tick started at ${new Date(status.runningSince).toLocaleString()}. The job processes up to ~${throughputPerRun.toLocaleString()} records per run and persists its cursor after every batch.`;
        case 'idle':
            return `Re-embed campaign is active on phase "${status.phaseLabel}". The last tick finished at ${new Date(status.lastTickEndedAt).toLocaleString()}. The next tick will run automatically (every 10 minutes) — or trigger it now below.`;
        case 'stalled':
            return status.reason === 'lock-stale'
                ? `A previous tick did not release the lock (held since ${new Date(status.since).toLocaleString()}). The owning process likely crashed — the next cron tick will reclaim and continue. ${status.detail}`
                : `Campaign appears stalled on phase "${status.phaseLabel}". ${status.detail} Check the circuit breaker and failure count above, then reset or manually trigger.`;
        case 'blocked':
            return `Last tick (${new Date(status.at).toLocaleString()}) could not proceed: ${status.reason}. The job will retry automatically on the next tick.`;
        case 'failed':
            return `Last tick (${new Date(status.at).toLocaleString()}) failed on phase "${status.phaseLabel}": ${status.reason}. The job will retry automatically on the next tick.`;
        case 'complete':
            return `All entity types have been re-embedded into Weaviate${status.completedAt ? ` on ${new Date(status.completedAt).toLocaleString()}` : ''}. Processed ${status.totals.processed.toLocaleString()} records across ${status.totals.ticks.toLocaleString()} ticks (${status.totals.errors.toLocaleString()} error(s)). To re-embed again, reset below.`;
    }
}

function ReembedProgressCard({
    reembed,
    reembedConfig,
    isTriggering,
    onTrigger,
    onResetAndReembed,
}: {
    reembed: ReembedData;
    reembedConfig: SyncStatusData['reembedConfig'];
    isTriggering: boolean;
    onTrigger: () => void;
    onResetAndReembed: () => void;
}) {
    const { status, state } = reembed;
    const throughputPerRun = reembedConfig.batchSize * reembedConfig.maxBatches;
    const isComplete = status.kind === 'complete';
    const isRunning = status.kind === 'running';
    const buttonLabel =
        status.kind === 'never-run' ? 'Start re-embed now' : isRunning ? 'Tick already running' : 'Run next batch now';

    const currentCursor =
        status.kind !== 'never-run' && status.kind !== 'complete' ? state?.cursors[status.phase] : undefined;

    const phaseProgress = status.kind !== 'never-run' && status.kind !== 'complete' ? status.phaseProgress : undefined;

    return (
        <Card size="small">
            <div className="mb-1 flex items-center gap-2">
                <Text strong>Re-embed</Text>
                <StatusTag status={status} />
                {state && state.totals.errors > 0 && (
                    <Tag color="red" variant="outlined">
                        {state.totals.errors.toLocaleString()} error(s) this campaign
                    </Tag>
                )}
            </div>
            <Text type="secondary" className="mb-2 block text-xs">
                {statusDescription(status, throughputPerRun)}
            </Text>
            {phaseProgress && <PhaseProgressBar progress={phaseProgress} />}
            {state && <OverallProgressBar state={state} status={status} />}
            <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-subtle-foreground text-xs">
                {status.kind !== 'never-run' && status.kind !== 'complete' && (
                    <span>
                        Phase: <strong>{status.phaseLabel}</strong>
                    </span>
                )}
                {currentCursor && <span>Cursor: {currentCursor}</span>}
                {state?.startedAt && <span>Started: {new Date(state.startedAt).toLocaleString()}</span>}
                {state?.lastTick?.endedAt && (
                    <span>
                        Last tick: {new Date(state.lastTick.endedAt).toLocaleString()}
                        {state.lastTick.durationMs !== undefined
                            ? ` (${Math.round(state.lastTick.durationMs / 1000)}s, ${state.lastTick.processed} processed)`
                            : ''}
                    </span>
                )}
                {state && (state.totals.ticks > 0 || state.totals.processed > 0) && (
                    <span>
                        Totals: {state.totals.processed.toLocaleString()} processed,{' '}
                        {state.totals.created.toLocaleString()} created, {state.totals.ticks.toLocaleString()} tick(s)
                    </span>
                )}
            </div>
            {isComplete ? (
                <Confirm
                    title="Reset and re-embed"
                    description="This will clear the completed re-embed state and immediately start re-embedding all entities from scratch. Use this after deploying content or vectoriser changes."
                    onOk={onResetAndReembed}
                    okText="Reset and re-embed"
                    okDanger
                >
                    <Button type="secondary" icon={<RefreshCw size={14} />} loading={isTriggering} size="small">
                        Reset and re-embed
                    </Button>
                </Confirm>
            ) : (
                <Confirm
                    title="Run re-embed batch now"
                    description={`This will immediately process up to ${reembedConfig.maxBatches} batches (~${throughputPerRun.toLocaleString()} records). The same job also runs automatically every 10 minutes — this button just triggers it now instead of waiting.`}
                    onOk={onTrigger}
                    okText="Run now"
                >
                    <Button
                        type="secondary"
                        icon={<Play size={14} />}
                        loading={isTriggering}
                        size="small"
                        disabled={isRunning}
                    >
                        {buttonLabel}
                    </Button>
                </Confirm>
            )}
        </Card>
    );
}

type BackfillData = SyncStatusData['backfill'];

function backfillStatusDescription(status: ReembedStatus, targetVersion: string): string {
    switch (status.kind) {
        case 'never-run':
            return `Backfills every record into the inactive ${targetVersion} collections (new embedding model) while the live collections keep serving traffic. Nothing runs until you start it — make sure migration 017 has created the ${targetVersion} collections first. Live sync writes are mirrored separately by dual-write; this campaign covers the historical corpus.`;
        case 'running':
            return `Currently backfilling ${status.phaseLabel} into ${targetVersion}. This tick started at ${new Date(status.runningSince).toLocaleString()}. Cursor state persists after every batch, so it is safe to leave running.`;
        case 'idle':
            return `Backfill campaign is active on phase "${status.phaseLabel}". The last tick finished at ${new Date(status.lastTickEndedAt).toLocaleString()}. The next tick runs automatically (every 10 minutes) — or trigger it now below.`;
        case 'stalled':
            return status.reason === 'lock-stale'
                ? `A previous tick did not release the lock (held since ${new Date(status.since).toLocaleString()}). The owning process likely crashed — the next cron tick will reclaim and continue. ${status.detail}`
                : `Campaign appears stalled on phase "${status.phaseLabel}". ${status.detail} Check the circuit breaker and failure count above, then reset or manually trigger.`;
        case 'blocked':
            return `Last tick (${new Date(status.at).toLocaleString()}) could not proceed: ${status.reason}`;
        case 'failed':
            return `Last tick (${new Date(status.at).toLocaleString()}) failed on phase "${status.phaseLabel}": ${status.reason}. The job will retry automatically on the next tick.`;
        case 'complete':
            return `The ${targetVersion} collections have been fully backfilled${status.completedAt ? ` on ${new Date(status.completedAt).toLocaleString()}` : ''}. Processed ${status.totals.processed.toLocaleString()} records across ${status.totals.ticks.toLocaleString()} ticks (${status.totals.errors.toLocaleString()} error(s)). Run the v1-vs-v2 spot checks before flipping WEAVIATE_COLLECTION_VERSION.`;
    }
}

function V2BackfillCard({
    backfill,
    mirrorHealth,
    reembedConfig,
    isTriggering,
    onTrigger,
    onResetAndBackfill,
}: {
    backfill: BackfillData;
    mirrorHealth: SyncStatusData['mirrorHealth'];
    reembedConfig: SyncStatusData['reembedConfig'];
    isTriggering: boolean;
    onTrigger: () => void;
    onResetAndBackfill: () => void;
}) {
    const { status, state, targetVersion } = backfill;
    const throughputPerRun = reembedConfig.batchSize * reembedConfig.maxBatches;
    const isComplete = status.kind === 'complete';
    const isRunning = status.kind === 'running';
    const mirrorHealthAvailable = mirrorHealth.status === 'available';
    const isForwardCutover = targetVersion === backfill.inactiveVersion;
    const readyForVersionChange = isForwardCutover ? mirrorHealth.readyForCutover : mirrorHealth.readyForRollback;
    const backfillStartBlocked = !backfill.dualWriteEstablished;
    const backfillStartPrerequisite = `Enable Weaviate dual-write to ${targetVersion} before starting the backfill campaign.`;
    const queueCounts = mirrorHealth.queueCounts;
    const cutoverBlockers = [
        ...(isForwardCutover && !isComplete ? [`The ${targetVersion} backfill campaign is not complete.`] : []),
        ...(!mirrorHealth.dualWriteEstablished
            ? [`Dual-write to ${mirrorHealth.targetVersion} is not established.`]
            : []),
        ...(mirrorHealth.failureCount === null
            ? [`${mirrorHealth.targetVersion} mirror failure count is unavailable.`]
            : mirrorHealth.failureCount > 0
              ? [`${mirrorHealth.failureCount.toLocaleString()} mirror failure(s) remain.`]
              : []),
        ...(mirrorHealth.outboxCount === null
            ? ['Mirror outbox count is unavailable.']
            : mirrorHealth.outboxCount > 0
              ? [`${mirrorHealth.outboxCount.toLocaleString()} DB outbox row(s) remain.`]
              : []),
        ...(mirrorHealth.deadLetterCount === null
            ? ['Mirror dead-letter count is unavailable.']
            : mirrorHealth.deadLetterCount > 0
              ? [`${mirrorHealth.deadLetterCount.toLocaleString()} dead-letter row(s) require operator action.`]
              : []),
        ...(queueCounts === null
            ? ['Mirror queue counts are unavailable.']
            : (Object.entries(queueCounts) as Array<[keyof NonNullable<typeof queueCounts>, number]>)
                  .filter(([, count]) => count > 0)
                  .map(
                      ([queueState, count]) =>
                          `${count.toLocaleString()} mirror queue job(s) are ${queueState === 'waiting' ? 'waiting' : queueState}.`,
                  )),
    ];
    const mirrorHealthDegraded =
        !mirrorHealth.dualWriteEstablished ||
        !mirrorHealthAvailable ||
        mirrorHealth.failureCount !== 0 ||
        mirrorHealth.outboxCount !== 0 ||
        mirrorHealth.deadLetterCount !== 0 ||
        queueCounts === null ||
        Object.values(queueCounts).some((count) => count > 0);
    const campaignInFlight = status.kind !== 'never-run' && status.kind !== 'complete';
    const cutoverAlertType = ((): 'info' | 'warning' | 'error' => {
        if (status.kind === 'never-run') return mirrorHealth.dualWriteEstablished ? 'warning' : 'info';
        if (campaignInFlight && mirrorHealthDegraded) return 'error';
        return 'warning';
    })();
    const cutoverAlertTitle = (() => {
        if (status.kind === 'never-run' && !mirrorHealth.dualWriteEstablished) {
            return 'Enable dual-write before starting the backfill';
        }
        if (status.kind === 'never-run') return 'Backfill must complete before cutover';
        if (!mirrorHealth.dualWriteEstablished) return 'Version change blocked because dual-write is disabled';
        return mirrorHealthAvailable
            ? 'Version change blocked by outstanding work'
            : 'Version-change readiness unavailable';
    })();
    const buttonLabel =
        status.kind === 'never-run' ? 'Start backfill now' : isRunning ? 'Tick already running' : 'Run next batch now';

    const currentCursor =
        status.kind !== 'never-run' && status.kind !== 'complete' ? state?.cursors[status.phase] : undefined;

    const phaseProgress = status.kind !== 'never-run' && status.kind !== 'complete' ? status.phaseProgress : undefined;

    return (
        <Card size="small">
            <div className="mb-1 flex items-center gap-2">
                <Text strong>Collection {targetVersion} backfill</Text>
                <StatusTag status={status} />
                <Tag color="purple" variant="outlined">
                    target: {targetVersion}
                </Tag>
                {state && state.totals.errors > 0 && (
                    <Tag color="red" variant="outlined">
                        {state.totals.errors.toLocaleString()} error(s) this campaign
                    </Tag>
                )}
            </div>
            <Text type="secondary" className="mb-2 block text-xs">
                {backfillStatusDescription(status, targetVersion)}
            </Text>
            <div className="mb-2 flex flex-wrap gap-2">
                <Tag
                    color={
                        mirrorHealth.failureCount === null ? 'orange' : mirrorHealth.failureCount > 0 ? 'red' : 'green'
                    }
                    variant="outlined"
                >
                    Mirror failures:{' '}
                    {mirrorHealth.failureCount === null ? 'unavailable' : mirrorHealth.failureCount.toLocaleString()}
                </Tag>
                <Tag
                    color={
                        mirrorHealth.outboxCount === null ? 'orange' : mirrorHealth.outboxCount > 0 ? 'red' : 'green'
                    }
                    variant="outlined"
                >
                    Mirror outbox:{' '}
                    {mirrorHealth.outboxCount === null ? 'unavailable' : mirrorHealth.outboxCount.toLocaleString()}
                </Tag>
                <Tag color={mirrorHealth.dualWriteEstablished ? 'green' : 'red'} variant="outlined">
                    Dual-write:{' '}
                    {mirrorHealth.dualWriteEstablished ? `enabled to ${mirrorHealth.targetVersion}` : 'disabled'}
                </Tag>
                <Tag
                    color={
                        mirrorHealth.deadLetterCount === null
                            ? 'orange'
                            : mirrorHealth.deadLetterCount > 0
                              ? 'red'
                              : 'green'
                    }
                    variant="outlined"
                >
                    Dead-letter rows:{' '}
                    {mirrorHealth.deadLetterCount === null
                        ? 'unavailable'
                        : mirrorHealth.deadLetterCount.toLocaleString()}
                </Tag>
                {queueCounts ? (
                    (Object.entries(queueCounts) as Array<[keyof NonNullable<typeof queueCounts>, number]>).map(
                        ([queueState, count]) => (
                            <Tag key={queueState} color={count > 0 ? 'red' : 'green'} variant="outlined">
                                Queue {queueState}: {count.toLocaleString()}
                            </Tag>
                        ),
                    )
                ) : (
                    <Tag color="orange" variant="outlined">
                        Mirror queue: unavailable
                    </Tag>
                )}
                {mirrorHealth.quarantinedCount > 0 && (
                    <Tag color="volcano" variant="outlined">
                        Terminal mirror failures: {mirrorHealth.quarantinedCount.toLocaleString()}
                    </Tag>
                )}
            </div>
            {!readyForVersionChange && (
                <Alert
                    className="mb-3"
                    type={cutoverAlertType}
                    showIcon
                    title={cutoverAlertTitle}
                    description={
                        <div>
                            <ul className="mb-0 list-disc pl-4">
                                {cutoverBlockers.map((blocker) => (
                                    <li key={blocker}>{blocker}</li>
                                ))}
                            </ul>
                            {mirrorHealth.outboxStale && mirrorHealth.oldestOutboxAt && (
                                <div className="mt-1">
                                    The oldest outbox item has been waiting since{' '}
                                    {new Date(mirrorHealth.oldestOutboxAt).toLocaleString()}.
                                </div>
                            )}
                            <div className="mt-1">
                                Do not change WEAVIATE_COLLECTION_VERSION until every prerequisite is clear.
                            </div>
                        </div>
                    }
                />
            )}
            {phaseProgress && <PhaseProgressBar progress={phaseProgress} />}
            {state && <OverallProgressBar state={state} status={status} variant="backfill" />}
            <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-subtle-foreground text-xs">
                {status.kind !== 'never-run' && status.kind !== 'complete' && (
                    <span>
                        Phase: <strong>{status.phaseLabel}</strong>
                    </span>
                )}
                {currentCursor && <span>Cursor: {currentCursor}</span>}
                {state?.startedAt && <span>Started: {new Date(state.startedAt).toLocaleString()}</span>}
                {state?.lastTick?.endedAt && (
                    <span>
                        Last tick: {new Date(state.lastTick.endedAt).toLocaleString()}
                        {state.lastTick.durationMs !== undefined
                            ? ` (${Math.round(state.lastTick.durationMs / 1000)}s, ${state.lastTick.processed} processed)`
                            : ''}
                    </span>
                )}
                {state && (state.totals.ticks > 0 || state.totals.processed > 0) && (
                    <span>
                        Totals: {state.totals.processed.toLocaleString()} processed,{' '}
                        {state.totals.created.toLocaleString()} created, {state.totals.ticks.toLocaleString()} tick(s)
                    </span>
                )}
            </div>
            {isComplete ? (
                <Confirm
                    title="Reset and backfill again"
                    description={`This will clear the completed backfill state and immediately start backfilling the ${targetVersion} collections from scratch. Existing ${targetVersion} objects are overwritten in place (writes are idempotent).`}
                    onOk={onResetAndBackfill}
                    okText="Reset and backfill"
                    okDanger
                    disabled={backfillStartBlocked}
                >
                    <Tooltip title={backfillStartBlocked ? backfillStartPrerequisite : undefined}>
                        <span>
                            <Button
                                type="secondary"
                                icon={<RefreshCw />}
                                loading={isTriggering}
                                size="small"
                                disabled={backfillStartBlocked}
                            >
                                Reset and backfill
                            </Button>
                        </span>
                    </Tooltip>
                </Confirm>
            ) : (
                <Confirm
                    title={status.kind === 'never-run' ? 'Start v2 backfill campaign' : 'Run backfill batch now'}
                    description={
                        status.kind === 'never-run'
                            ? `This starts the campaign that re-embeds every record into the ${targetVersion} collections (embedding spend applies). Requires migration 017 to have created the collections — the tick will report "blocked" if they are missing. Once started, it continues automatically every 10 minutes until complete.`
                            : `This will immediately process up to ${reembedConfig.maxBatches} batches (~${throughputPerRun.toLocaleString()} records). The same job also runs automatically every 10 minutes — this button just triggers it now instead of waiting.`
                    }
                    onOk={onTrigger}
                    okText={status.kind === 'never-run' ? 'Start backfill' : 'Run now'}
                    disabled={backfillStartBlocked}
                >
                    <Tooltip title={backfillStartBlocked ? backfillStartPrerequisite : undefined}>
                        <span>
                            <Button
                                type="secondary"
                                icon={<Play />}
                                loading={isTriggering}
                                size="small"
                                disabled={isRunning || backfillStartBlocked}
                            >
                                {buttonLabel}
                            </Button>
                        </span>
                    </Tooltip>
                </Confirm>
            )}
        </Card>
    );
}

function EntitySyncStatusCard({
    entitySyncStatus,
    onRefresh,
    isFetching,
}: {
    entitySyncStatus: SyncStatusData['entitySyncStatus'];
    onRefresh: () => void;
    isFetching: boolean;
}) {
    const entities = [
        { key: 'obligation', label: 'Obligations', data: entitySyncStatus.obligation },
        { key: 'masterObligation', label: 'Master obligations', data: entitySyncStatus.masterObligation },
        { key: 'faq', label: 'FAQs', data: entitySyncStatus.faq },
        {
            key: 'aiClassificationExample',
            label: 'AI classification examples',
            data: entitySyncStatus.aiClassificationExample,
        },
        { key: 'fileChunks', label: 'File chunks', data: entitySyncStatus.fileChunks },
        { key: 'marketPdsSections', label: 'PDS sections', data: entitySyncStatus.marketPdsSections },
        { key: 'incident', label: 'Incidents', data: entitySyncStatus.incident },
        { key: 'complaint', label: 'Complaints', data: entitySyncStatus.complaint },
    ] as const;

    const totalEntities = entities.reduce((sum, e) => sum + e.data.total, 0);
    const totalSynced = entities.reduce((sum, e) => sum + e.data.synced, 0);
    const totalPending = entities.reduce((sum, e) => sum + e.data.pending, 0);
    const totalFailed = entities.reduce((sum, e) => sum + e.data.failed, 0);
    const totalUnknown = entities.reduce((sum, e) => sum + ('unknown' in e.data ? e.data.unknown : 0), 0);

    return (
        <Card size="small">
            <div className="mb-1 flex items-center justify-between">
                <Text strong>Entity sync status</Text>
                <Button type="secondary" size="small" loading={isFetching} onClick={onRefresh}>
                    Refresh
                </Button>
            </div>
            <Text type="secondary" className="mb-3 block text-xs">
                Shows how many Postgres entities have been synced to Weaviate. <strong>Synced</strong> = embedded in
                Weaviate and searchable. <strong>Pending</strong> = awaiting embedding (new or re-embed in progress).{' '}
                <strong>Failed</strong> = sync error, will be retried by reconciliation. <strong>Unknown</strong> =
                missing or unexpected status. Obligations count only <strong>PUBLISHED</strong> rows, since drafts and
                review-stage obligations are not embedded. File chunk counts exclude files with no generated embeddings
                and show missing chunk status separately.
                {totalEntities > 0 && (
                    <span className="ml-2">
                        Total: {totalSynced}/{totalEntities} synced
                        {totalPending > 0 && `, ${totalPending} pending`}
                        {totalFailed > 0 && `, ${totalFailed} failed`}
                        {totalUnknown > 0 && `, ${totalUnknown} unknown`}.
                    </span>
                )}
            </Text>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {entities.map(({ key, label, data }) => {
                    const processing = 'processing' in data ? data.processing : 0;
                    const noChunks = 'noChunks' in data ? data.noChunks : 0;
                    const missingStatus = 'missingStatus' in data ? data.missingStatus : 0;
                    const unknown = 'unknown' in data ? data.unknown : 0;
                    const statusCount = [
                        data.synced > 0,
                        data.pending > 0,
                        processing > 0,
                        data.failed > 0,
                        unknown > 0,
                    ].filter(Boolean).length;
                    const isSingleStatus = statusCount <= 1;
                    const showSpecialEmptyState = data.total === 0 && (noChunks > 0 || missingStatus > 0);

                    return (
                        <div key={key} className="rounded-lg border p-3">
                            <div className="mb-1 text-subtle-foreground text-xs">{label}</div>
                            {showSpecialEmptyState ? (
                                <div className="font-semibold text-lg">
                                    {missingStatus > 0 ? missingStatus : noChunks}{' '}
                                    <span className="font-normal text-sm text-subtle-foreground">
                                        {missingStatus > 0 ? 'missing status' : 'no chunks'}
                                    </span>
                                </div>
                            ) : isSingleStatus ? (
                                // Single status — show count with status inline
                                <div className="font-semibold text-lg">
                                    {data.total}{' '}
                                    {data.synced > 0 && (
                                        <span className="font-normal text-green-600 text-sm">synced</span>
                                    )}
                                    {data.pending > 0 && (
                                        <span className="font-normal text-orange-500 text-sm">pending</span>
                                    )}
                                    {processing > 0 && (
                                        <span className="font-normal text-blue-600 text-sm">processing</span>
                                    )}
                                    {data.failed > 0 && (
                                        <span className="font-normal text-red-500 text-sm">failed</span>
                                    )}
                                    {unknown > 0 && (
                                        <span className="font-normal text-sm text-subtle-foreground">unknown</span>
                                    )}
                                    {data.total === 0 && (
                                        <span className="font-normal text-sm text-subtle-foreground">entities</span>
                                    )}
                                </div>
                            ) : (
                                // Mixed statuses — show total + breakdown tags
                                <>
                                    <div className="mb-1 font-semibold text-lg">{data.total}</div>
                                    <div className="flex flex-wrap gap-1">
                                        {data.synced > 0 && (
                                            <Tag color="green" variant="outlined" className="!text-xs">
                                                {data.synced} synced
                                            </Tag>
                                        )}
                                        {data.pending > 0 && (
                                            <Tag color="orange" variant="outlined" className="!text-xs">
                                                {data.pending} pending
                                            </Tag>
                                        )}
                                        {processing > 0 && (
                                            <Tag color="blue" variant="outlined" className="!text-xs">
                                                {processing} processing
                                            </Tag>
                                        )}
                                        {data.failed > 0 && (
                                            <Tag color="red" variant="outlined" className="!text-xs">
                                                {data.failed} failed
                                            </Tag>
                                        )}
                                        {unknown > 0 && (
                                            <Tag variant="outlined" className="!text-xs">
                                                {unknown} unknown
                                            </Tag>
                                        )}
                                        {missingStatus > 0 && (
                                            <Tag variant="outlined" className="!text-xs">
                                                {missingStatus} missing status
                                            </Tag>
                                        )}
                                        {noChunks > 0 && (
                                            <Tag variant="outlined" className="!text-xs">
                                                {noChunks} no chunks
                                            </Tag>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}

// ─── Re-embed Configuration ──────────────────────────────────────────────────

function ReembedConfigSection() {
    const { trpc, trpcClient } = useTrpc();
    const { data, isLoading, refetch } = trpc.admin.weaviate.getSyncStatus.useQuery(undefined, {
        refetchInterval: 30_000,
    });

    const [form, setForm] = useState<{
        batchSize: number;
        maxBatches: number;
        concurrency: number;
        timeBudgetMs: number;
    } | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize form from data
    const config = data?.reembedConfig;
    const currentForm = form ?? config;

    const handleSave = async () => {
        if (!currentForm) return;
        try {
            setIsSaving(true);
            await trpcClient.admin.weaviate.updateReembedConfig.mutate(currentForm);
            message.success('Re-embed configuration updated. Changes take effect on next run.');
            setForm(null);
            refetch();
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsSaving(false);
        }
    };

    const hasChanges =
        form !== null &&
        config &&
        (form.batchSize !== config.batchSize ||
            form.maxBatches !== config.maxBatches ||
            form.concurrency !== config.concurrency ||
            form.timeBudgetMs !== config.timeBudgetMs);

    return (
        <div className="mb-6">
            <Divider>
                <Space>
                    <Settings size={16} />
                    <Title level={5} className="!mb-0">
                        Re-embed configuration
                    </Title>
                </Space>
            </Divider>

            {isLoading || !currentForm ? (
                <Card size="small">
                    <Skeleton active />
                </Card>
            ) : (
                <Card size="small">
                    <div className="mb-4 flex items-start justify-between gap-4">
                        <Text type="secondary">
                            Tune re-embed job performance. Changes are saved to the database and take effect on the next
                            scheduled run — no server restart needed.
                        </Text>
                        <Button
                            type="secondary"
                            size="small"
                            onClick={() => {
                                setForm(null);
                                refetch();
                            }}
                        >
                            Refresh
                        </Button>
                    </div>

                    <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Batch size
                            </Text>
                            <InputNumber
                                min={50}
                                max={2000}
                                step={50}
                                value={currentForm.batchSize}
                                onChange={(v) => setForm({ ...currentForm, batchSize: v ?? 400 })}
                                className="w-full"
                            />
                            <Text type="secondary" className="mt-1 block text-xs">
                                Records per batch (50-2000)
                            </Text>
                        </div>
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Max batches per run
                            </Text>
                            <InputNumber
                                min={1}
                                max={50}
                                value={currentForm.maxBatches}
                                onChange={(v) => setForm({ ...currentForm, maxBatches: v ?? 10 })}
                                className="w-full"
                            />
                            <Text type="secondary" className="mt-1 block text-xs">
                                Batches per 10-min run (1-50)
                            </Text>
                        </div>
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Concurrency
                            </Text>
                            <InputNumber
                                min={1}
                                max={50}
                                value={currentForm.concurrency}
                                onChange={(v) => setForm({ ...currentForm, concurrency: v ?? 10 })}
                                className="w-full"
                            />
                            <Text type="secondary" className="mt-1 block text-xs">
                                Parallel Weaviate writes (1-50)
                            </Text>
                        </div>
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Time budget
                            </Text>
                            <InputNumber
                                min={60000}
                                max={1800000}
                                step={60000}
                                value={currentForm.timeBudgetMs}
                                onChange={(v) => setForm({ ...currentForm, timeBudgetMs: v ?? 480000 })}
                                className="w-full"
                                formatter={(v) => `${Math.round((v ?? 0) / 60000)} min`}
                                parser={(v) => Number.parseInt(v?.replace(/\D/g, '') ?? '8', 10) * 60000}
                            />
                            <Text type="secondary" className="mt-1 block text-xs">
                                Max duration per run (1-30 min)
                            </Text>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Button
                            type="primary"
                            size="small"
                            onClick={handleSave}
                            loading={isSaving}
                            disabled={!hasChanges}
                        >
                            Save configuration
                        </Button>
                        {hasChanges && (
                            <Button size="small" onClick={() => setForm(null)}>
                                Reset
                            </Button>
                        )}
                        {currentForm && (
                            <Text type="secondary" className="text-xs">
                                Throughput: ~{(currentForm.batchSize * currentForm.maxBatches).toLocaleString()}{' '}
                                records/run
                            </Text>
                        )}
                    </div>
                </Card>
            )}
        </div>
    );
}

// ─── Obligation Embeddings Backfill ──────────────────────────────────────────

const BACKFILL_ORG_ID_INPUT_ID = 'weaviate-backfill-org-id-input';

function ObligationBackfillSection() {
    const { trpc } = useTrpc();
    // Kept as an integer string (InputNumber stringMode) so large org IDs survive
    // the round-trip to BigInt without JS number-precision loss.
    const [orgId, setOrgId] = useState<string | null>(null);

    const { mutate: generateObligationEmbeddings, isPending } = trpc.admin.obligation.generateEmbeddings.useMutation({
        onSuccess: (data) => {
            if (data.jobId) {
                showJobSuccessModal({
                    jobId: data.jobId,
                    title: 'Embeddings generation queued',
                    message: 'The obligation embeddings generation has been queued for processing.',
                    description:
                        'The job re-syncs every published obligation for the organisation and may take a few minutes depending on the number of obligations.',
                });
            }
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    return (
        <div className="mb-6">
            <Divider>
                <Space>
                    <Brain size={16} />
                    <Title level={5} className="!mb-0">
                        Obligation embeddings
                    </Title>
                </Space>
            </Divider>

            <Card size="small">
                <Text type="secondary" className="mb-3 block">
                    Queue a targeted backfill that re-syncs every published obligation for one organisation into
                    Weaviate. Reconciliation covers this automatically over time — use this when you need a fast
                    backfill, e.g. after seeding a new or demo organisation.
                </Text>
                <Space>
                    <AntSpace.Compact className="w-56">
                        <label
                            htmlFor={BACKFILL_ORG_ID_INPUT_ID}
                            className="inline-flex items-center whitespace-nowrap rounded-l-md border border-separator border-r-0 bg-muted px-3 text-subtle-foreground"
                        >
                            Org ID
                        </label>
                        <InputNumber<string>
                            id={BACKFILL_ORG_ID_INPUT_ID}
                            min="1"
                            precision={0}
                            stringMode
                            value={orgId ?? undefined}
                            onChange={(v) => setOrgId(v ?? null)}
                            className="flex-1"
                        />
                    </AntSpace.Compact>
                    <Button
                        type="secondary"
                        icon={<Play size={14} />}
                        loading={isPending}
                        disabled={!orgId}
                        onClick={() => {
                            if (orgId) {
                                generateObligationEmbeddings({ orgId: BigInt(orgId) });
                            }
                        }}
                    >
                        Generate obligation embeddings
                    </Button>
                </Space>
            </Card>
        </div>
    );
}

// ─── Tenant Management ───────────────────────────────────────────────────────

function TenantManagementSection() {
    const { trpc, trpcClient } = useTrpc();

    const [tenantPrefix, setTenantPrefix] = useState('');
    const [previewResult, setPreviewResult] = useState<{
        collections: Array<{
            collection: string;
            totalTenants: number;
            matchingTenants: number;
            matchingObjectCount: number;
            tenantNames: string[];
            tenantObjectCounts: Array<{ tenant: string; objectCount: number }>;
        }>;
    } | null>(null);

    const listTenantsMutation = trpc.admin.weaviate.listTenants.useMutation();

    const deleteSelectedTenants = async (input: RouterInputs['admin']['weaviate']['deleteTenants']) => {
        try {
            message.loading('Deleting tenants...');
            const result = await trpcClient.admin.weaviate.deleteTenants.mutate(input);
            message.destroy();

            const totalDeleted = result.collections.reduce((sum, c) => sum + c.tenantsDeleted, 0);
            message.success(`Deleted ${totalDeleted} tenant(s) across ${result.collections.length} collections`);

            setPreviewResult(null);
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        }
    };

    const handlePreview = async () => {
        if (!tenantPrefix.trim()) {
            message.warning('Please enter a tenant name prefix');
            return;
        }

        try {
            const result = await listTenantsMutation.mutateAsync({ prefix: tenantPrefix.trim() });
            setPreviewResult(result);
        } catch (error) {
            errorMessage.show(error);
        }
    };

    const { showConfirmationModal: showPurgeModal, contextHolder: purgeContextHolder } = useDeleteConfirmation({
        title: 'Purge tenant content',
        content:
            'This will delete all objects (documents) from tenants matching the prefix across all Weaviate collections. The tenants themselves will be preserved. Sync state (reconciliation, re-embed, orphan cleanup) will also be reset.',
        okText: 'Purge content',
        entityDisplayName: 'prefix',
        onConfirm: async ({ confirmationText }) => {
            try {
                message.loading('Purging tenant content...');
                const result = await trpcClient.admin.weaviate.purgeTenantContent.mutate({
                    prefix: tenantPrefix.trim(),
                    confirmationText,
                });
                message.destroy();

                const totalPurged = result.collections.reduce((sum, c) => sum + c.totalObjectsDeleted, 0);
                const syncMsg = result.syncStateReset > 0 ? `, reset ${result.syncStateReset} sync state(s)` : '';
                message.success(
                    `Purged ${totalPurged} object(s) across ${result.collections.length} collections${syncMsg}`,
                );
            } catch (error) {
                message.destroy();
                errorMessage.show(error);
            }
        },
    });

    const { showConfirmationModal: showExactDeleteModal, contextHolder: exactDeleteContextHolder } =
        useDeleteConfirmation({
            title: 'Delete exact Weaviate tenant',
            content:
                'This will permanently delete only the tenant whose name exactly matches this value across all Weaviate collections. This action cannot be undone.',
            okText: 'Delete tenant',
            entityDisplayName: 'tenant name',
            onConfirm: async ({ confirmationText }) => {
                await deleteSelectedTenants({
                    mode: 'exact',
                    tenantName: tenantPrefix.trim(),
                    confirmationText,
                });
            },
        });

    const { showConfirmationModal: showBulkDeleteModal, contextHolder: bulkDeleteContextHolder } =
        useDeleteConfirmation({
            title: 'Bulk delete Weaviate tenants',
            content:
                'This bulk operation will permanently delete every tenant whose name starts with this prefix across all Weaviate collections. This action cannot be undone.',
            okText: 'Delete tenants',
            entityDisplayName: 'prefix',
            onConfirm: async ({ confirmationText }) => {
                await deleteSelectedTenants({
                    mode: 'prefix',
                    prefix: tenantPrefix.trim(),
                    confirmationText,
                });
            },
        });

    const totalMatching = previewResult?.collections.reduce((sum, c) => sum + c.matchingTenants, 0) ?? 0;
    const exactTenantExists =
        previewResult?.collections.some((collection) => collection.tenantNames.includes(tenantPrefix.trim())) ?? false;

    return (
        <div className="mb-6">
            {purgeContextHolder}
            {exactDeleteContextHolder}
            {bulkDeleteContextHolder}

            <Divider>
                <Space>
                    <Trash2 size={16} />
                    <Title level={5} className="!mb-0">
                        Tenant management
                    </Title>
                </Space>
            </Divider>

            <Card size="small">
                <Text type="secondary" className="mb-3 block">
                    Search for tenants by name prefix across all collections in the current environment. Tenants are
                    named <code>org_1</code>, <code>org_2</code>, <code>system_global</code>, etc. Useful for removing
                    specific organisation data or cleaning up orphaned tenants. Use exact deletion for a single tenant;
                    prefix deletion is an explicitly labelled bulk operation.
                </Text>

                <div className="mb-3">
                    <AntSpace.Compact className="w-full">
                        <label
                            htmlFor={TENANT_PREFIX_INPUT_ID}
                            className="inline-flex items-center whitespace-nowrap rounded-l-md border border-separator border-r-0 bg-muted px-3 text-subtle-foreground"
                        >
                            Tenant name prefix
                        </label>
                        <Input
                            id={TENANT_PREFIX_INPUT_ID}
                            placeholder="e.g. org_"
                            value={tenantPrefix}
                            onChange={(e) => {
                                setTenantPrefix(e.target.value);
                                setPreviewResult(null);
                            }}
                        />
                    </AntSpace.Compact>
                </div>

                <Space>
                    <Tooltip title="List all tenants matching this prefix across Weaviate collections">
                        <Button
                            type="secondary"
                            icon={<Search size={16} />}
                            loading={listTenantsMutation.isPending}
                            disabled={!tenantPrefix.trim()}
                            onClick={handlePreview}
                        >
                            Preview matching tenants
                        </Button>
                    </Tooltip>

                    {previewResult && (
                        <Tooltip title="Delete all objects from matching tenants but preserve the tenants themselves. Resets sync state.">
                            <Button
                                type="secondary"
                                icon={<Eraser size={16} />}
                                disabled={!tenantPrefix.trim()}
                                onClick={() => showPurgeModal(tenantPrefix.trim())}
                            >
                                Purge content
                            </Button>
                        </Tooltip>
                    )}

                    {previewResult && exactTenantExists && (
                        <Tooltip title="Permanently delete only the tenant with this exact name across all collections">
                            <Button
                                type="danger"
                                icon={<Trash2 />}
                                disabled={!tenantPrefix.trim()}
                                onClick={() => showExactDeleteModal(tenantPrefix.trim())}
                            >
                                Delete exact tenant
                            </Button>
                        </Tooltip>
                    )}

                    {previewResult && totalMatching > 0 && (
                        <Tooltip title="Bulk delete every tenant whose name starts with this prefix across all collections">
                            <Button
                                type="danger"
                                icon={<Trash2 />}
                                disabled={!tenantPrefix.trim()}
                                onClick={() => showBulkDeleteModal(tenantPrefix.trim())}
                            >
                                Bulk delete {totalMatching} match(es)
                            </Button>
                        </Tooltip>
                    )}
                </Space>

                {previewResult && (
                    <div className="mt-4">
                        {totalMatching === 0 ? (
                            <Alert type="info" title={`No tenants found matching prefix "${tenantPrefix}"`} showIcon />
                        ) : (
                            <Collapse
                                items={previewResult.collections
                                    .filter((c) => c.matchingTenants > 0)
                                    .map((c) => ({
                                        key: c.collection,
                                        label: (
                                            <Space>
                                                <span>{c.collection}</span>
                                                <Tag color="orange" variant="outlined">
                                                    {c.matchingTenants} matching
                                                </Tag>
                                                <Tag color="blue" variant="outlined">
                                                    {c.matchingObjectCount.toLocaleString()} objects
                                                </Tag>
                                                <Text type="secondary">({c.totalTenants} total)</Text>
                                            </Space>
                                        ),
                                        children: (
                                            <Space wrap>
                                                {c.tenantObjectCounts.map((t) => (
                                                    <Tag key={t.tenant} variant="outlined">
                                                        {t.tenant}{' '}
                                                        <Text type="secondary" className="text-xs">
                                                            ({t.objectCount.toLocaleString()})
                                                        </Text>
                                                    </Tag>
                                                ))}
                                            </Space>
                                        ),
                                    }))}
                            />
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}

// ─── Sync State Reset ────────────────────────────────────────────────────────

type SyncJob = {
    key:
        | 'weaviate_reconciliation_state'
        | 'weaviate_reembed_state'
        | 'weaviate_cleanup_state'
        | 'weaviate_v2_backfill_state';
    label: string;
    description: string;
};

const SYNC_JOBS: SyncJob[] = [
    {
        key: 'weaviate_reconciliation_state',
        label: 'Reconciliation',
        description: 'ensures all Postgres records exist in Weaviate',
    },
    {
        key: 'weaviate_reembed_state',
        label: 'Re-embed',
        description: 'regenerates embeddings for all records',
    },
    {
        key: 'weaviate_cleanup_state',
        label: 'Orphan cleanup',
        description: 'removes Weaviate objects without matching Postgres records',
    },
    {
        key: 'weaviate_v2_backfill_state',
        label: 'V2 backfill',
        description: 'stops the v2 backfill campaign until an operator starts it again (opt-in job)',
    },
];

function SyncStateSection() {
    const { trpcClient } = useTrpc();
    const [resettingKeys, setResettingKeys] = useState<Set<string>>(new Set());
    const [selectedResetJob, setSelectedResetJob] = useState<SyncJob>();

    const resetSyncState = async (settings?: SyncJob['key'][], label?: string) => {
        const resetKey = settings ? settings.join(',') : 'all';
        const displayLabel = label ?? 'all sync state';

        try {
            setResettingKeys((prev) => new Set(prev).add(resetKey));
            message.loading(`Resetting ${displayLabel}...`);
            const result = await trpcClient.admin.weaviate.resetSyncState.mutate({
                confirmationText: 'RESET',
                settings,
            });
            message.destroy();

            const deletedCount = result.results.filter((r) => r.deleted).length;
            const skippedCount = result.results.filter((r) => !r.deleted).length;

            if (deletedCount > 0) {
                message.success(
                    `Reset ${deletedCount} sync state(s)${skippedCount > 0 ? `, ${skippedCount} not found` : ''}`,
                );
            } else {
                message.info(`No ${displayLabel} state found to reset`);
            }
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        } finally {
            setResettingKeys((prev) => {
                const next = new Set(prev);
                next.delete(resetKey);
                return next;
            });
        }
    };

    const { showConfirmationModal: showResetAllModal, contextHolder: resetAllContextHolder } = useDeleteConfirmation({
        title: 'Reset all Weaviate sync state',
        content:
            'This will delete all background sync job cursors (reconciliation, re-embed, orphan cleanup, v2 backfill). Each job will restart from scratch on its next scheduled run — except the v2 backfill, which stays stopped until an operator starts it again.',
        okText: 'Reset all',
        entityDisplayName: 'confirmation',
        onConfirm: () => resetSyncState(),
    });

    const { showConfirmationModal: showResetSingleModal, contextHolder: resetSingleContextHolder } =
        useDeleteConfirmation<SyncJob>({
            title: 'Reset sync state',
            content:
                selectedResetJob?.key === 'weaviate_v2_backfill_state'
                    ? 'This stops the v2 backfill campaign. It will stay stopped until an operator starts it again.'
                    : 'This sync job will restart from scratch on its next scheduled run.',
            okText: 'Reset',
            entityDisplayName: 'confirmation',
            onConfirm: async ({ params: job }) => {
                await resetSyncState([job.key], job.label.toLowerCase());
            },
        });

    return (
        <div className="mb-6">
            {resetAllContextHolder}
            {resetSingleContextHolder}

            <Divider>
                <Space>
                    <RefreshCw size={16} />
                    <Title level={5} className="!mb-0">
                        Sync state
                    </Title>
                </Space>
            </Divider>

            <Card size="small">
                <Text type="secondary" className="mb-3 block">
                    The Weaviate sync engine uses four background jobs that persist cursor state in AppSettings.
                    Resetting clears a job&apos;s cursor so it restarts from the beginning on its next scheduled run,
                    except for the opt-in v2 backfill campaign, which stays stopped until an operator starts it again.
                </Text>

                <Space direction="vertical" className="mb-3 w-full">
                    {SYNC_JOBS.map((job) => (
                        <div key={job.key} className="flex items-center justify-between">
                            <Text>
                                <strong>{job.label}</strong> — {job.description}
                            </Text>
                            <Button
                                size="small"
                                icon={<RefreshCw size={14} />}
                                loading={resettingKeys.has(job.key)}
                                onClick={() => {
                                    setSelectedResetJob(job);
                                    showResetSingleModal('RESET', job);
                                }}
                            >
                                Reset
                            </Button>
                        </div>
                    ))}
                </Space>

                <Button
                    type="danger"
                    icon={<RefreshCw size={16} />}
                    loading={resettingKeys.has('all')}
                    onClick={() => showResetAllModal('RESET')}
                >
                    Reset all sync state
                </Button>
            </Card>
        </div>
    );
}

// ─── Quarantined Records ─────────────────────────────────────────────────────

type QuarantinedListData = RouterOutputs['admin']['weaviate']['listQuarantined'];
type QuarantinedRow = QuarantinedListData['rows'][number];

const ALL_COLLECTIONS_VALUE = '__all__';

function QuarantinedRecordsSection() {
    const { trpc, trpcClient, trpcUtils } = useTrpc();
    const [collectionFilter, setCollectionFilter] = useState<string>(ALL_COLLECTIONS_VALUE);
    const [pendingRowAction, setPendingRowAction] = useState<bigint | null>(null);
    const [pendingBulk, setPendingBulk] = useState<'release' | 'delete' | null>(null);

    const { data, isLoading, isFetching, error, refetch } = trpc.admin.weaviate.listQuarantined.useQuery(
        collectionFilter === ALL_COLLECTIONS_VALUE ? undefined : { collection: collectionFilter },
        { refetchInterval: 60_000 },
    );

    const handleRelease = async (id: bigint) => {
        try {
            setPendingRowAction(id);
            await trpcClient.admin.weaviate.releaseQuarantine.mutate({ id });
            message.success('Quarantine released. Reconciliation will retry on its next pass.');
            await trpcUtils.admin.weaviate.listQuarantined.invalidate();
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setPendingRowAction(null);
        }
    };

    const handleDeleteEntry = async (id: bigint) => {
        try {
            setPendingRowAction(id);
            await trpcClient.admin.weaviate.deleteQuarantineEntry.mutate({ id });
            message.success('Orphaned quarantine entry deleted.');
            await trpcUtils.admin.weaviate.listQuarantined.invalidate();
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setPendingRowAction(null);
        }
    };

    const handleBulkRelease = async () => {
        try {
            setPendingBulk('release');
            const result = await trpcClient.admin.weaviate.bulkReleaseQuarantine.mutate({
                confirmationText: 'RELEASE',
                ...(collectionFilter === ALL_COLLECTIONS_VALUE ? {} : { collection: collectionFilter }),
            });
            message.success(`Released ${result.releasedCount} quarantined record(s).`);
            await trpcUtils.admin.weaviate.listQuarantined.invalidate();
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setPendingBulk(null);
        }
    };

    const handleBulkDeleteOrphans = async () => {
        try {
            setPendingBulk('delete');
            const result = await trpcClient.admin.weaviate.bulkDeleteOrphanedQuarantine.mutate({
                confirmationText: 'DELETE',
                ...(collectionFilter === ALL_COLLECTIONS_VALUE ? {} : { collection: collectionFilter }),
            });
            const skipped = result.skippedCount > 0 ? `, ${result.skippedCount} skipped (source still exists)` : '';
            message.success(`Deleted ${result.deletedCount} orphaned record(s)${skipped}.`);
            await trpcUtils.admin.weaviate.listQuarantined.invalidate();
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setPendingBulk(null);
        }
    };

    const totalCount = data?.totals.total ?? 0;
    const filteredRows = data?.rows ?? [];
    const collectionOptions = data?.totals.byCollection ?? [];
    // True matching count for the active filter — use this for confirmation
    // copy and the "showing N of M" footer. `filteredRows.length` is capped
    // at the query limit so it lies when the matching set is larger.
    const matchingTotal =
        collectionFilter === ALL_COLLECTIONS_VALUE
            ? totalCount
            : (collectionOptions.find((c) => c.collection === collectionFilter)?.count ?? 0);
    const isTruncated = matchingTotal > filteredRows.length;

    return (
        <div className="mb-6">
            <Divider>
                <Space>
                    <ShieldAlert size={16} />
                    <Title level={5} className="!mb-0">
                        Quarantined records
                    </Title>
                </Space>
            </Divider>

            {isLoading ? (
                <Card size="small">
                    <Skeleton active />
                </Card>
            ) : error || !data ? (
                <Card size="small">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Space direction="vertical" size="small">
                                <Text type="secondary">Could not load quarantined records.</Text>
                                <Button type="secondary" size="small" onClick={() => refetch()}>
                                    Retry
                                </Button>
                            </Space>
                        }
                    />
                </Card>
            ) : totalCount === 0 ? (
                <Card size="small">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No quarantined records — sync engine is healthy."
                    />
                </Card>
            ) : matchingTotal === 0 ? (
                <Card size="small">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Space direction="vertical" size="small">
                                <Text type="secondary">No quarantined records match the selected collection.</Text>
                                <Button
                                    type="secondary"
                                    size="small"
                                    onClick={() => setCollectionFilter(ALL_COLLECTIONS_VALUE)}
                                >
                                    Show all collections
                                </Button>
                            </Space>
                        }
                    />
                </Card>
            ) : (
                <Card size="small">
                    <Text type="secondary" className="mb-3 block text-xs">
                        Records that have failed to sync to Weaviate after the configured retry cap. Reconciliation
                        skips them until you act. <strong>Release</strong> clears the quarantine flag so the next
                        reconciliation pass retries (use after fixing the root cause). <strong>Delete entry</strong>{' '}
                        removes the failure ledger row entirely — only available when the underlying Postgres source is
                        gone, because reconciliation would never revisit it anyway.
                    </Text>

                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <Tag color="orange" variant="outlined">
                            {totalCount} total
                        </Tag>
                        {collectionOptions.map((c) => (
                            <Tag key={c.collection} variant="outlined">
                                {c.collection}: {c.count}
                            </Tag>
                        ))}
                    </div>

                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <Select
                            value={collectionFilter}
                            onChange={setCollectionFilter}
                            className="w-64"
                            options={[
                                { label: 'All collections', value: ALL_COLLECTIONS_VALUE },
                                ...collectionOptions.map((c) => ({
                                    label: `${c.collection} (${c.count})`,
                                    value: c.collection,
                                })),
                            ]}
                        />
                        <Button type="secondary" size="small" loading={isFetching} onClick={() => refetch()}>
                            Refresh
                        </Button>
                        <div className="ml-auto flex flex-wrap gap-2">
                            <Confirm
                                title="Release all matching"
                                description={
                                    <span>
                                        This will clear the quarantine flag on <strong>{matchingTotal}</strong>{' '}
                                        record(s) and reset their retry counter.{' '}
                                        {collectionFilter === ALL_COLLECTIONS_VALUE
                                            ? 'Across every collection.'
                                            : `In collection ${collectionFilter}.`}{' '}
                                        Use this only after fixing the underlying root cause — otherwise they will just
                                        re-quarantine on the next failed retry.
                                    </span>
                                }
                                onOk={handleBulkRelease}
                                okText="Release"
                            >
                                <Button
                                    type="secondary"
                                    size="small"
                                    icon={<RefreshCw size={14} />}
                                    loading={pendingBulk === 'release'}
                                >
                                    Release all
                                    {collectionFilter !== ALL_COLLECTIONS_VALUE && ` in ${collectionFilter}`}
                                </Button>
                            </Confirm>
                            <Confirm
                                title="Delete all orphaned entries"
                                description={
                                    <span>
                                        This will scan{' '}
                                        {collectionFilter === ALL_COLLECTIONS_VALUE
                                            ? 'all quarantined records'
                                            : `quarantined records in ${collectionFilter}`}
                                        , check whether their underlying Postgres rows still exist, and{' '}
                                        <strong>delete only the orphans</strong>. Records whose source still exists are
                                        left untouched — release those instead.
                                    </span>
                                }
                                onOk={handleBulkDeleteOrphans}
                                okText="Delete orphans"
                                okDanger
                            >
                                <Button
                                    type="secondary"
                                    size="small"
                                    icon={<Trash2 size={14} />}
                                    loading={pendingBulk === 'delete'}
                                    danger
                                >
                                    Delete orphans
                                </Button>
                            </Confirm>
                        </div>
                    </div>

                    <Table<QuarantinedRow>
                        size="small"
                        rowKey={(row) => row.id.toString()}
                        dataSource={filteredRows}
                        pagination={false}
                        columns={[
                            {
                                title: 'Collection',
                                dataIndex: 'collection',
                                render: (_value, row) => (
                                    <div className="flex flex-col">
                                        <span className="font-medium">{row.collection}</span>
                                        <Text type="secondary" className="text-xs">
                                            {row.entityType}
                                        </Text>
                                    </div>
                                ),
                            },
                            {
                                title: 'Org',
                                dataIndex: 'orgId',
                                width: 80,
                                render: (_value, row) => row.orgId.toString(),
                            },
                            {
                                title: 'Postgres ID',
                                dataIndex: 'postgresId',
                                width: 200,
                                render: (_value, row) => (
                                    <Tooltip title={row.postgresId.toString()}>
                                        <span className="font-mono text-xs">
                                            {row.postgresId.toString().slice(0, 12)}…
                                        </span>
                                    </Tooltip>
                                ),
                            },
                            {
                                title: 'Source',
                                dataIndex: 'postgresRowExists',
                                width: 130,
                                render: (_value, row) => <ExistenceTag value={row.postgresRowExists} />,
                            },
                            {
                                title: 'Error',
                                dataIndex: 'errorMessage',
                                render: (_value, row) => (
                                    <Tooltip title={row.errorMessage}>
                                        <span className="line-clamp-2 text-xs">{row.errorMessage}</span>
                                    </Tooltip>
                                ),
                            },
                            {
                                title: 'Retries',
                                dataIndex: 'retryCount',
                                width: 80,
                                align: 'center',
                                render: (_value, row) => row.retryCount,
                            },
                            {
                                title: 'Quarantined',
                                dataIndex: 'quarantinedAt',
                                width: 160,
                                render: (_value, row) =>
                                    row.quarantinedAt ? (
                                        <Tooltip title={new Date(row.quarantinedAt).toLocaleString()}>
                                            <span className="text-xs">
                                                {formatRelativeAge(new Date(row.quarantinedAt))}
                                            </span>
                                        </Tooltip>
                                    ) : null,
                            },
                            {
                                title: 'Actions',
                                key: 'actions',
                                width: 220,
                                render: (_value, row) => (
                                    <Space size="small">
                                        {row.postgresRowExists !== 'missing' && (
                                            <Confirm
                                                title="Release quarantine"
                                                description="Clears the quarantine flag and resets retry count. The next reconciliation pass will retry the sync."
                                                onOk={() => handleRelease(row.id)}
                                                okText="Release"
                                            >
                                                <Button
                                                    size="small"
                                                    type="secondary"
                                                    icon={<RefreshCw size={12} />}
                                                    loading={pendingRowAction === row.id}
                                                >
                                                    Release
                                                </Button>
                                            </Confirm>
                                        )}
                                        {row.postgresRowExists === 'missing' && (
                                            <Confirm
                                                title="Delete orphaned entry"
                                                description="The underlying Postgres row no longer exists, so reconciliation will never revisit this. Deleting the failure ledger row is safe."
                                                onOk={() => handleDeleteEntry(row.id)}
                                                okText="Delete"
                                                okDanger
                                            >
                                                <Button
                                                    size="small"
                                                    type="secondary"
                                                    icon={<Trash2 size={12} />}
                                                    loading={pendingRowAction === row.id}
                                                    danger
                                                >
                                                    Delete entry
                                                </Button>
                                            </Confirm>
                                        )}
                                    </Space>
                                ),
                            },
                        ]}
                    />

                    {isTruncated && (
                        <Text type="secondary" className="mt-3 block text-xs">
                            Showing {filteredRows.length} of {matchingTotal} matching record(s). Use bulk actions or
                            filter by collection to act on the rest.
                        </Text>
                    )}
                </Card>
            )}
        </div>
    );
}

function ExistenceTag({ value }: { value: QuarantinedRow['postgresRowExists'] }) {
    if (value === 'exists') {
        return (
            <Tag color="green" variant="outlined">
                <Space size="micro">
                    <CheckCircle2 size={12} />
                    Source exists
                </Space>
            </Tag>
        );
    }
    if (value === 'missing') {
        return (
            <Tag color="red" variant="outlined">
                <Space size="micro">
                    <AlertTriangle size={12} />
                    Orphan
                </Space>
            </Tag>
        );
    }
    return (
        <Tag variant="outlined">
            <Space size="micro">
                <HelpCircle size={12} />
                Unknown
            </Space>
        </Tag>
    );
}

function formatRelativeAge(when: Date): string {
    const diffMs = Date.now() - when.getTime();
    const seconds = Math.round(diffMs / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
}
